// src/components/LiveTop.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import styles from "../css/LiveTop.module.css";
import Footer from "./Footer";

const fallbackLogo = "/images/logo-placeholder.png";

// Lazy-load and render each league’s live matches
const LeagueGroup = ({
  league,
  matches,
  userTimeZone,
  follows,
  onFollowChange,
}) => {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry], o) => {
        if (entry.isIntersecting) {
          setVisible(true);
          o.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={containerRef} className={styles.leagueGroup}>
      <header className={styles.leagueHeader}>
        <img
          src={league.logo}
          alt={league.name}
          className={styles.leagueLogo}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = fallbackLogo;
          }}
          loading="lazy"
        />
        <h2 className={styles.leagueName}>{league.name}</h2>
      </header>

      {visible ? (
        <ul className={styles.list}>
          {matches.map((m) => {
            const date = new Date(m.fixture.date);
            const formattedDate = date.toLocaleString(undefined, {
              timeZone: userTimeZone,
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
            const venueName = m.fixture.venue.name?.trim();
            const venueCity = m.fixture.venue.city?.trim();
            const locationText =
              venueName || venueCity
                ? [venueName, venueCity].filter(Boolean).join(", ")
                : "Not available";

            const { home, away } = m.teams;
            // which team is currently followed?
            const selected = follows.includes(home.id)
              ? home.id.toString()
              : follows.includes(away.id)
              ? away.id.toString()
              : "";

            return (
              <li key={m.fixture.id} className={styles.matchItem}>
                <div className={styles.matchCard}>
                  {/* Date Header */}
                  <div className={styles.date}>{formattedDate}</div>

                  {/* Score Row */}
                  <div className={styles.matchContent}>
                    <div className={styles.teamBlock}>
                      <img
                        src={home.logo}
                        alt={home.name}
                        className={styles.teamLogo}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = fallbackLogo;
                        }}
                        loading="lazy"
                      />
                      <span className={styles.teamName}>{home.name}</span>
                    </div>

                    <div className={styles.score}>
                      {m.goals.home} – {m.goals.away}
                    </div>

                    <div className={styles.teamBlock}>
                      <span className={styles.teamName}>{away.name}</span>
                      <img
                        src={away.logo}
                        alt={away.name}
                        className={styles.teamLogo}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = fallbackLogo;
                        }}
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Footer Meta + Follow Dropdown */}
                  <div className={styles.matchMeta}>
                    <span className={styles.status}>
                      Status: {m.fixture.status.long}
                    </span>
                    <span className={styles.location}>{locationText}</span>
                    <select
                      className={styles.followSelect}
                      value={selected}
                      onChange={(e) => onFollowChange(Number(e.target.value))}
                      aria-label="Follow team"
                    >
                      <option value="">Follow Team</option>
                      <option value={home.id}>{home.name}</option>
                      <option value={away.id}>{away.name}</option>
                    </select>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className={styles.placeholder}>Loading live fixtures…</div>
      )}
    </section>
  );
};

const LiveTop = () => {
  const [matches, setMatches] = useState([]);
  const [follows, setFollows] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("followedTeams")) || [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    let mounted = true;
    const fetchLive = () => {
      axios
        .get("https://fetchrss.visiontrek.io/api-football/live-fixtures")
        .then((resp) => {
          if (mounted) {
            setMatches(resp.data.fixtures || []);
            setError(null);
          }
        })
        .catch((err) => {
          if (mounted) setError(err);
        })
        .finally(() => {
          if (mounted) setLoading(false);
        });
    };

    fetchLive();
    const id = setInterval(fetchLive, 10000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  const handleFollowChange = (teamId) => {
    setFollows((prev) => {
      const updated = prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId];
      localStorage.setItem("followedTeams", JSON.stringify(updated));
      return updated;
    });
  };

  if (loading)
    return (
      <div className={styles.loadingWrapper}>
        <div className={styles.loader} />
      </div>
    );
  if (error)
    return (
      <div className={styles.error}>
        Error fetching live fixtures: {error.message}
      </div>
    );
  if (matches.length === 0)
    return (
      <div className={styles.noMatchesWrapper}>
        <div className={styles.noMatchesBox}>No matches playing right now</div>
        <Footer />
      </div>
    );

  const grouped = matches.reduce((acc, m) => {
    const id = m.league.id;
    if (!acc[id]) acc[id] = { league: m.league, matches: [] };
    acc[id].matches.push(m);
    return acc;
  }, {});

  return (
    <div className={styles.wrapper}>
      {Object.values(grouped).map(({ league, matches }) => (
        <LeagueGroup
          key={league.id}
          league={league}
          matches={matches}
          userTimeZone={tz}
          follows={follows}
          onFollowChange={handleFollowChange}
        />
      ))}
      <Footer />
    </div>
  );
};

export default LiveTop;
